import type { ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  BaseAvatar,
  BaseButton,
  cn,
  type BaseAppearance,
} from '../base';

export type UserAccountMenuRadius =
  | 'default'
  | 'none'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | 'full';

export type UserAccountMenuSide = 'top' | 'right' | 'bottom' | 'left';
export type UserAccountMenuTriggerVariant = 'avatar' | 'profile';

export interface UserAccountMenuUser {
  avatarAlt?: string;
  avatarUrl?: string;
  initials?: string;
  name?: string;
  secondaryText?: ReactNode;
}

export interface UserAccountMenuSelectorOption {
  disabled?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  value: string;
}

export interface UserAccountMenuSelectorConfig {
  contentWidthClassName?: string;
  disabled?: boolean;
  id?: string;
  icon?: ReactNode;
  label: ReactNode;
  onChange?: (value: string) => void;
  options: UserAccountMenuSelectorOption[];
  value: string;
  visible?: boolean;
}

export interface UserAccountMenuItem {
  disabled?: boolean;
  icon?: ReactNode;
  id: string;
  label: ReactNode;
  onSelect?: () => void;
  separatorBefore?: boolean;
  shortcut?: ReactNode;
  tone?: 'default' | 'danger';
  visible?: boolean;
}

export interface UserAccountMenuClassNames {
  avatar?: string;
  content?: string;
  dangerItem?: string;
  item?: string;
  itemIcon?: string;
  label?: string;
  root?: string;
  secondaryText?: string;
  separator?: string;
  subContent?: string;
  subTrigger?: string;
  trigger?: string;
  userName?: string;
}

export interface UserAccountMenuProps {
  align?: 'start' | 'center' | 'end';
  appearance?: BaseAppearance;
  ariaLabel?: string;
  className?: string;
  classNames?: UserAccountMenuClassNames;
  contentWidthClassName?: string;
  fallbackName?: string;
  items?: UserAccountMenuItem[];
  menuRadius?: UserAccountMenuRadius;
  selectors?: UserAccountMenuSelectorConfig[];
  side?: UserAccountMenuSide;
  sideOffset?: number;
  triggerEndIcon?: ReactNode;
  triggerTitle?: string;
  triggerVariant?: UserAccountMenuTriggerVariant;
  user: UserAccountMenuUser;
}

const defaultAppearance: BaseAppearance = {
  preset: 'soft',
  density: 'compact',
  radius: 'lg',
  border: 'subtle',
  background: 'soft',
};

const radiusClassNames: Record<UserAccountMenuRadius, string> = {
  default: 'rounded-md',
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
};

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

function renderIcon(icon: ReactNode, className?: string) {
  if (!icon) return null;
  return (
    <span className={cn('inline-flex h-4 w-4 shrink-0 items-center justify-center', className)}>
      {icon}
    </span>
  );
}

function getCurrentOptionLabel(selector: UserAccountMenuSelectorConfig) {
  return selector.options.find((option) => option.value === selector.value)?.label ?? selector.value;
}

function UserAccountMenuSelector({
  classNames,
  menuRadius,
  selector,
}: {
  classNames?: UserAccountMenuClassNames;
  menuRadius: UserAccountMenuRadius;
  selector: UserAccountMenuSelectorConfig;
}) {
  if (selector.visible === false || selector.options.length === 0) return null;

  const radiusClass = radiusClassNames[menuRadius];
  const currentLabel = getCurrentOptionLabel(selector);

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        disabled={selector.disabled}
        className={cn(
          'relative flex cursor-pointer select-none items-center gap-2 px-2 py-2 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[state=open]:bg-[hsl(var(--accent))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          radiusClass,
          classNames?.subTrigger,
        )}
      >
        {renderIcon(selector.icon)}
        <span className="min-w-0 truncate">{selector.label}</span>
        <span className="ml-auto min-w-0 max-w-[7rem] truncate text-xs text-[hsl(var(--muted-foreground))]">
          {currentLabel}
        </span>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          className={cn(
            'z-50 min-w-40 border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1.5 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95',
            radiusClass,
            selector.contentWidthClassName,
            classNames?.subContent,
          )}
        >
          <DropdownMenu.RadioGroup value={selector.value} onValueChange={selector.onChange}>
            {selector.options.map((option) => (
              <DropdownMenu.RadioItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex cursor-pointer select-none items-center gap-2 px-2 py-2 pl-8 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  radiusClass,
                  classNames?.item,
                )}
              >
                <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex h-4 w-4 items-center justify-center">
                  <CheckIcon />
                </DropdownMenu.ItemIndicator>
                {renderIcon(option.icon, classNames?.itemIcon)}
                <span className="min-w-0 truncate">{option.label}</span>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

export function UserAccountMenu({
  align = 'end',
  appearance = defaultAppearance,
  ariaLabel,
  className,
  classNames,
  contentWidthClassName = 'w-56',
  fallbackName = 'User',
  items = [],
  menuRadius = 'default',
  selectors = [],
  side = 'bottom',
  sideOffset = 8,
  triggerEndIcon,
  triggerTitle,
  triggerVariant = 'avatar',
  user,
}: UserAccountMenuProps) {
  const displayName = user.name?.trim() || fallbackName;
  const radiusClass = radiusClassNames[menuRadius];
  const visibleSelectors = selectors.filter(
    (selector) => selector.visible !== false && selector.options.length > 0,
  );
  const visibleItems = items.filter((item) => item.visible !== false);
  const hasMenuBody = visibleSelectors.length > 0 || visibleItems.length > 0;

  const avatar = (
    <BaseAvatar
      appearance={appearance}
      alt={user.avatarAlt ?? displayName}
      className={cn('h-8 w-8', classNames?.avatar)}
      initials={user.initials}
      name={displayName}
      size="sm"
      src={user.avatarUrl}
    />
  );
  const isProfileTrigger = triggerVariant === 'profile';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <BaseButton
          appearance={appearance}
          ariaLabel={ariaLabel ?? displayName}
          className={cn(
            isProfileTrigger
              ? 'h-auto w-full justify-start rounded-lg border-transparent bg-transparent px-2 py-2 text-left shadow-none transition-all hover:bg-[hsl(var(--muted))]/60'
              : 'h-9 w-9 overflow-visible rounded-full border-transparent bg-transparent p-0 shadow-none transition-all hover:ring-2 hover:ring-[hsl(var(--ring))]/20',
            className,
            classNames?.root,
            classNames?.trigger,
          )}
          classNames={{
            icon: 'h-8 w-8',
            label: isProfileTrigger ? 'min-w-0 flex-1 text-left' : undefined,
          }}
          endIcon={isProfileTrigger ? (triggerEndIcon ?? <MoreIcon />) : undefined}
          icon={avatar}
          iconOnly={!isProfileTrigger}
          label={
            isProfileTrigger ? (
              <span className="flex min-w-0 flex-col items-start">
                <span className="max-w-full truncate text-sm font-medium leading-none text-[hsl(var(--foreground))]">
                  {displayName}
                </span>
                {user.secondaryText ? (
                  <span className="mt-1 max-w-full truncate text-xs leading-none text-[hsl(var(--muted-foreground))]">
                    {user.secondaryText}
                  </span>
                ) : null}
              </span>
            ) : undefined
          }
          title={triggerTitle ?? displayName}
          tone="muted"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          sideOffset={sideOffset}
          className={cn(
            'z-50 border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1.5 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95',
            contentWidthClassName,
            radiusClass,
            classNames?.content,
          )}
        >
          <DropdownMenu.Label className={cn('px-2 py-1.5 font-normal', classNames?.label)}>
            <div className="flex min-w-0 items-center gap-2">
              {avatar}
              <div className="flex min-w-0 flex-col">
                <span className={cn('truncate text-sm font-medium leading-none', classNames?.userName)}>
                  {displayName}
                </span>
                {user.secondaryText ? (
                  <span
                    className={cn(
                      'truncate pt-1 text-xs leading-none text-[hsl(var(--muted-foreground))]',
                      classNames?.secondaryText,
                    )}
                  >
                    {user.secondaryText}
                  </span>
                ) : null}
              </div>
            </div>
          </DropdownMenu.Label>

          {hasMenuBody ? (
            <DropdownMenu.Separator
              className={cn('my-1 h-px bg-[hsl(var(--border))]', classNames?.separator)}
            />
          ) : null}

          {visibleSelectors.map((selector, index) => (
            <UserAccountMenuSelector
              key={selector.id ?? `selector-${index}`}
              classNames={classNames}
              menuRadius={menuRadius}
              selector={selector}
            />
          ))}

          {visibleSelectors.length > 0 && visibleItems.length > 0 ? (
            <DropdownMenu.Separator
              className={cn('my-1 h-px bg-[hsl(var(--border))]', classNames?.separator)}
            />
          ) : null}

          {visibleItems.map((item) => (
            <div key={item.id}>
              {item.separatorBefore ? (
                <DropdownMenu.Separator
                  className={cn('my-1 h-px bg-[hsl(var(--border))]', classNames?.separator)}
                />
              ) : null}
              <DropdownMenu.Item
                disabled={item.disabled}
                onSelect={item.disabled ? undefined : item.onSelect}
                className={cn(
                  'relative flex cursor-pointer select-none items-center gap-2 px-2 py-2 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  radiusClass,
                  item.tone === 'danger' &&
                    'text-[hsl(var(--destructive))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]',
                  classNames?.item,
                  item.tone === 'danger' && classNames?.dangerItem,
                )}
              >
                {renderIcon(item.icon, classNames?.itemIcon)}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {item.shortcut ? (
                  <span className="ml-3 shrink-0 text-xs text-[hsl(var(--muted-foreground))]">
                    {item.shortcut}
                  </span>
                ) : null}
              </DropdownMenu.Item>
            </div>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
